import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { X, CreditCard, MessageCircle, Crown, Zap, Shield, Sparkles } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useAuth, SubscriptionPlan } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { isAndroidTWA } from "@/utils/runtimeContext";

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
}

type PlanDetails = {
  name: string;
  price: string; 
  period: string;
  originalPrice?: string;
  savings?: string;
  features: string[];
};

type Plan = "monthly" | "quarterly" | "annual";

const planDetails: Record<Plan, PlanDetails> = {
  monthly: {
    name: "MENSAL",
    price: "R$ 34,90",
    period: "/mês",
    features: ["Acesso completo", "10 palpites/dia", "Suporte incluso"]
  },
  quarterly: {
    name: "TRIMESTRAL",
    price: "R$ 54,90", 
    period: "/trimestre",
    originalPrice: "R$ 74,70",
    savings: "Economia R$ 19,80",
    features: ["Tudo do mensal", "Software Turbinado", "3 meses de acesso"]
  },
  annual: {
    name: "ANUAL",
    price: "R$ 174,90",
    period: "/ano", 
    originalPrice: "R$ 298,80",
    savings: "Economia R$ 123,90",
    features: ["Melhor custo-benefício", "Acesso prioritário", "12 meses de acesso"]
  }
};

const UpgradeModal: React.FC<UpgradeModalProps> = ({ open, onClose }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [selectedPlan, setSelectedPlan] = useState<Plan>("quarterly");
  const [isProcessingStripe, setIsProcessingStripe] = useState(false);

  const handleStripePayment = async () => {
    if (!user) {
      toast({
        title: "Erro",
        description: "Você precisa estar logado para assinar.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessingStripe(true);
    
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      
      if (!token) {
        throw new Error("Token de autenticação não encontrado");
      }
      
      const planMapping: Record<Plan, SubscriptionPlan> = {
        monthly: "basic",
        quarterly: "premium", 
        annual: "vip"
      };
      
      const billingMapping: Record<Plan, string> = {
        monthly: "monthly",
        quarterly: "quarterly",
        annual: "annual"
      };

      const { data, error } = await supabase.functions.invoke('create-mp-preference', {
        body: { 
          plan: planMapping[selectedPlan],
          billingPeriod: billingMapping[selectedPlan]
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (error) throw new Error(error.message || "Erro ao processar pagamento");
      
      if (data?.url) {
        window.open(data.url, '_blank');
        onClose();
      } else {
        throw new Error("URL de checkout não encontrada");
      }
    } catch (error) {
      console.error("Erro no pagamento Stripe:", error);
      toast({
        title: "Erro ao processar pagamento",
        description: error instanceof Error ? error.message : "Tente novamente",
        variant: "destructive",
      });
    } finally {
      setIsProcessingStripe(false);
    }
  };

  const handlePixPayment = () => {
    const plan = planDetails[selectedPlan];
    const message = `Olá, quero ativar meu acesso ao IA Palpite Guru via Pix no plano ${plan.name}.`;
    const encodedMessage = encodeURIComponent(message);
    
    // Detectar se é mobile ou desktop
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    const whatsappUrl = isMobile 
      ? `whatsapp://send?phone=5511987654321&text=${encodedMessage}`
      : `https://web.whatsapp.com/send?phone=5511987654321&text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!open || isAndroidTWA()) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4"
      onClick={handleBackdropClick}
    >
      <Card className="w-full max-w-sm sm:max-w-md mx-auto bg-card text-card-foreground relative shadow-2xl rounded-xl animate-in fade-in zoom-in duration-300 max-h-[90vh] overflow-y-auto mobile-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-primary/80 p-4 sm:p-5 rounded-t-xl relative overflow-hidden text-primary-foreground">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="absolute right-2 top-2 text-primary-foreground hover:bg-primary-foreground/10 rounded-full w-8 h-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-primary-foreground/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <Crown className="h-6 w-6 text-gold-400" />
            </div>
            <h2 className="text-lg sm:text-xl font-bold text-primary-foreground mb-1">
              Teste Expirado!
            </h2>
            <p className="text-primary-foreground/90 text-sm">
              Continue com acesso completo ao IA Palpite Guru
            </p>
          </div>
          
          {/* Decorative elements */}
          <div className="absolute -top-2 -right-2 w-20 h-20 bg-primary-foreground/10 rounded-full"></div>
          <div className="absolute -bottom-3 -left-3 w-16 h-16 bg-primary-foreground/10 rounded-full"></div>
        </div>

        <CardContent className="p-4 sm:p-6">
          {/* Plan Selection */}
          <div className="mb-6">
            <h3 className="font-semibold mb-3 text-center">Escolha seu plano:</h3>
            <RadioGroup 
              value={selectedPlan} 
              onValueChange={(value: Plan) => setSelectedPlan(value)}
              className="space-y-3"
            >
              {(Object.entries(planDetails) as [Plan, typeof planDetails[Plan]][]).map(([key, plan]) => (
                <div key={key}>
                  <Label 
                    htmlFor={key}
                    className="cursor-pointer"
                  >
                    <Card className={`p-3 sm:p-4 border transition-all rounded-lg ${
                      selectedPlan === key 
                        ? 'ring-2 ring-primary bg-primary/5 border-transparent' 
                        : 'border-border hover:border-muted'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem value={key} id={key} className="text-primary" />
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-sm">{plan.name}</p>
                              {key === "quarterly" && (
                                <span className="bg-success/15 text-success text-xs px-2 py-1 rounded-full font-medium">
                                  POPULAR
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {plan.features[0]}
                            </p>
                            {plan.savings && (
                              <p className="text-xs text-success font-medium">
                                {plan.savings}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg">{plan.price}</p>
                          <p className="text-xs text-muted-foreground">{plan.period}</p>
                        </div>
                      </div>
                    </Card>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Benefits */}
          <div className="bg-muted rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="font-medium text-sm">Acesso completo inclui:</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-1">
                <Zap className="h-3 w-3 text-success" />
                <span>Palpites ilimitados</span>
              </div>
              <div className="flex items-center gap-1">
                <Shield className="h-3 w-3 text-primary" />
                <span>Suporte prioritário</span>
              </div>
              <div className="flex items-center gap-1">
                <Crown className="h-3 w-3 text-gold-500" />
                <span>Software Turbinado</span>
              </div>
              <div className="flex items-center gap-1">
                <Sparkles className="h-3 w-3 text-primary" />
                <span>Sem limitações</span>
              </div>
            </div>
          </div>

          {/* Payment Buttons */}
          <div className="space-y-3">
            <Button
              onClick={handleStripePayment}
              disabled={isProcessingStripe}
              className="w-full btn-gradient text-primary-foreground font-semibold py-3 rounded-md disabled:opacity-50"
            >
              {isProcessingStripe ? (
                <span>Processando...</span>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Pagar com Cartão
                </>
              )}
            </Button>
            
            <Button
              onClick={handlePixPayment}
              variant="outline"
              className="w-full border-2 border-success text-success hover:bg-success/10 font-semibold py-3 rounded-md"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Pagar via Pix (WhatsApp)
            </Button>
          </div>
          
          <p className="text-xs text-center text-muted-foreground mt-4">
            Pagamento seguro • Cancele quando quiser
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default UpgradeModal;